// Audio - Web Audio API sound generation (no audio files needed)

import { getMuted, setMuted } from './storage.js';

const MASTER_VOLUME = 0.3;

export class GameAudio {
  constructor() {
    this.ctx = null;
    this.masterGain = null;
    this._initialized = false;
    this._windNode = null;
    this._windGain = null;
    this.muted = getMuted();
  }

  // Must be called from a user gesture (click/keypress)
  init() {
    if (this._initialized) return;
    try {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = this.muted ? 0 : MASTER_VOLUME;
      this.masterGain.connect(this.ctx.destination);
      this._initialized = true;
    } catch (e) {
      // Audio not available - silently degrade
    }
  }

  toggleMute() {
    this.muted = !this.muted;
    setMuted(this.muted);
    if (this.masterGain) {
      this.masterGain.gain.value = this.muted ? 0 : MASTER_VOLUME;
    }
    return this.muted;
  }

  _ensureCtx() {
    if (!this._initialized) this.init();
    if (!this.ctx) return false;
    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
    return true;
  }

  // Attack swoosh - short noise sweep
  playAttack() {
    if (!this._ensureCtx()) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(200, t);
    osc.frequency.exponentialRampToValueAtTime(80, t + 0.12);
    gain.gain.setValueAtTime(0.15, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
    osc.connect(gain);
    gain.connect(this.masterGain);
    osc.start(t);
    osc.stop(t + 0.15);
  }

  // Hit impact - short thud
  playHit() {
    if (!this._ensureCtx()) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'square';
    osc.frequency.setValueAtTime(150, t);
    osc.frequency.exponentialRampToValueAtTime(40, t + 0.08);
    gain.gain.setValueAtTime(0.2, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.1);
    osc.connect(gain);
    gain.connect(this.masterGain);
    osc.start(t);
    osc.stop(t + 0.1);
  }

  // Coin pickup - bright ascending tones
  playCoin() {
    if (!this._ensureCtx()) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(600, t);
    osc.frequency.setValueAtTime(900, t + 0.05);
    osc.frequency.setValueAtTime(1200, t + 0.1);
    gain.gain.setValueAtTime(0.12, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
    osc.connect(gain);
    gain.connect(this.masterGain);
    osc.start(t);
    osc.stop(t + 0.2);
  }

  // Pickup chime - bright two-note arpeggio for medkit / buff drops.
  // Distinct from playCoin (which stays the coin sound) so a buff reads as
  // "you gained an effect" rather than "you got money".
  playPickup() {
    if (!this._ensureCtx()) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(523, t);       // C5
    osc.frequency.setValueAtTime(784, t + 0.08); // G5
    osc.frequency.setValueAtTime(1047, t + 0.16); // C6
    gain.gain.setValueAtTime(0.14, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
    osc.connect(gain);
    gain.connect(this.masterGain);
    osc.start(t);
    osc.stop(t + 0.3);
  }

  // Guard death - descending noise
  playGuardDeath() {
    if (!this._ensureCtx()) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(300, t);
    osc.frequency.exponentialRampToValueAtTime(50, t + 0.3);
    gain.gain.setValueAtTime(0.12, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
    osc.connect(gain);
    gain.connect(this.masterGain);
    osc.start(t);
    osc.stop(t + 0.35);
  }

  // Dash whoosh - quick rising sine sweep
  playDash() {
    if (!this._ensureCtx()) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(320, t);
    osc.frequency.exponentialRampToValueAtTime(120, t + 0.14);
    gain.gain.setValueAtTime(0.12, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.16);
    osc.connect(gain);
    gain.connect(this.masterGain);
    osc.start(t);
    osc.stop(t + 0.16);
  }

  // Player hurt - short distorted noise
  playPlayerHurt() {
    if (!this._ensureCtx()) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'square';
    osc.frequency.setValueAtTime(200, t);
    osc.frequency.setValueAtTime(100, t + 0.05);
    osc.frequency.setValueAtTime(150, t + 0.1);
    gain.gain.setValueAtTime(0.15, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
    osc.connect(gain);
    gain.connect(this.masterGain);
    osc.start(t);
    osc.stop(t + 0.2);
  }

  // Start ambient wind loop
  startWind() {
    if (!this._ensureCtx()) return;
    if (this._windNode) return; // already playing

    // Use noise buffer for wind
    const bufferSize = this.ctx.sampleRate * 2;
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    this._windNode = this.ctx.createBufferSource();
    this._windNode.buffer = buffer;
    this._windNode.loop = true;

    // Low-pass filter for wind-like sound
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 200;
    filter.Q.value = 0.5;

    this._windGain = this.ctx.createGain();
    this._windGain.gain.value = 0.04;

    this._windNode.connect(filter);
    filter.connect(this._windGain);
    this._windGain.connect(this.masterGain);
    this._windNode.start();
  }

  stopWind() {
    if (this._windNode) {
      this._windNode.stop();
      this._windNode = null;
      this._windGain = null;
    }
  }

  // Rising filtered-noise sweep used at the start of the rebirth
  // dissolution phase. Reads as the desert inhaling toward the player.
  playRebirthWhoosh() {
    if (!this._ensureCtx()) return;
    const t = this.ctx.currentTime;
    const dur = 1.6;
    const bufferSize = Math.floor(this.ctx.sampleRate * dur);
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.Q.value = 6;
    filter.frequency.setValueAtTime(120, t);
    filter.frequency.exponentialRampToValueAtTime(1800, t + dur);
    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(0.18, t + dur * 0.7);
    gain.gain.exponentialRampToValueAtTime(0.001, t + dur);
    src.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);
    src.start(t);
    src.stop(t + dur);
  }

  // Low resonant bell tone — single strike at the "descent" boundary.
  // Fundamental + a couple of inharmonic partials gives it weight
  // without sounding like a clean sine.
  playRebirthBell() {
    if (!this._ensureCtx()) return;
    const t = this.ctx.currentTime;
    const dur = 2.5;
    const partials = [
      { f: 110, a: 0.30 },
      { f: 220, a: 0.18 },
      { f: 261, a: 0.10 }, // inharmonic for "bell" timbre
      { f: 440, a: 0.06 },
    ];
    for (const P of partials) {
      const osc = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(P.f, t);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(P.a, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, t + dur);
      osc.connect(g);
      g.connect(this.masterGain);
      osc.start(t);
      osc.stop(t + dur);
    }
  }

  // Five-thump heartbeat that accelerates through the early cutscene —
  // distant at first, faster as the singularity approaches. Mimics the
  // anxiety pattern of a body about to die. Schedules all thumps at
  // future Web Audio times from a single call so no JS timers needed.
  playRebirthHeartbeat() {
    if (!this._ensureCtx()) return;
    const t0 = this.ctx.currentTime;
    // (offset, intensity) pairs — gap shrinks, intensity climbs.
    const beats = [
      [0.00, 0.16],
      [0.55, 0.18],
      [1.00, 0.22],
      [1.35, 0.28],
      [1.60, 0.36],
    ];
    for (const [off, amp] of beats) {
      const t = t0 + off;
      const osc = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(70, t);
      osc.frequency.exponentialRampToValueAtTime(35, t + 0.18);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(amp, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.22);
      osc.connect(g);
      g.connect(this.masterGain);
      osc.start(t);
      osc.stop(t + 0.25);
    }
  }

  // Sub-bass rumble that creeps in during the descent phase and
  // resolves into the bell strike. Lowpassed noise + a slowly drifting
  // very low sine give a "ground unsettled" feel without being musical.
  playRebirthRumble() {
    if (!this._ensureCtx()) return;
    const t = this.ctx.currentTime;
    const dur = 2.0;

    // Sub-bass sine wobble.
    const sub = this.ctx.createOscillator();
    const subG = this.ctx.createGain();
    sub.type = 'sine';
    sub.frequency.setValueAtTime(32, t);
    sub.frequency.linearRampToValueAtTime(22, t + dur);
    subG.gain.setValueAtTime(0.0001, t);
    subG.gain.exponentialRampToValueAtTime(0.22, t + dur * 0.85);
    subG.gain.exponentialRampToValueAtTime(0.001, t + dur);
    sub.connect(subG);
    subG.connect(this.masterGain);
    sub.start(t);
    sub.stop(t + dur);

    // Low-passed noise layer.
    const bufferSize = Math.floor(this.ctx.sampleRate * dur);
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 80;
    filter.Q.value = 1.2;
    const ng = this.ctx.createGain();
    ng.gain.setValueAtTime(0.0001, t);
    ng.gain.exponentialRampToValueAtTime(0.12, t + dur * 0.85);
    ng.gain.exponentialRampToValueAtTime(0.001, t + dur);
    src.connect(filter);
    filter.connect(ng);
    ng.connect(this.masterGain);
    src.start(t);
    src.stop(t + dur);
  }

  // Sustained major-third chord pad that rises into the light/lotus
  // phase. Three detuned sines per voice for a slow choral texture.
  playRebirthChoir() {
    if (!this._ensureCtx()) return;
    const t = this.ctx.currentTime;
    const dur = 2.2;
    const voices = [220, 277.18, 329.63]; // A3 + C#4 + E4 (major)
    const detunes = [-7, 0, 7];
    for (const f of voices) {
      for (const cents of detunes) {
        const osc = this.ctx.createOscillator();
        const g = this.ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(f, t);
        osc.detune.setValueAtTime(cents, t);
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.05, t + 0.6);
        g.gain.linearRampToValueAtTime(0.05, t + dur - 0.4);
        g.gain.exponentialRampToValueAtTime(0.001, t + dur);
        osc.connect(g);
        g.connect(this.masterGain);
        osc.start(t);
        osc.stop(t + dur);
      }
    }
  }
}
